/**
 * Students Page
 *
 * Страница со списком учеников.
 * Серверная фильтрация, поиск, сортировка, пагинация.
 * По паттерну Organizations.js.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Title, Text, Loader, Center, Stack, Box, Group, Pagination, Select, Button,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import StudentsTable from '../components/Students/StudentsTable';
import StudentViewModal from '../components/Students/StudentViewModal';
import StudentFormModal from '../components/Students/StudentFormModal';
import tokens from '../theme/designTokens';
import { useSessionState } from '../hooks/useSessionState';
import { getStudents, getStudentFilterOptions } from '../services/api/studentsService';

// Дефолтные фильтры (пустые Set = показать все)
const DEFAULT_FILTERS = {
  organizations: new Set(),
  study_groups: new Set(),
  parents: new Set(),
  is_active: new Set(),
};

const filtersSerialize = (filters) => {
  const obj = {};
  Object.entries(filters).forEach(([k, v]) => {
    obj[k] = v instanceof Set ? [...v] : [];
  });
  return obj;
};

const filtersDeserialize = (raw) => {
  const obj = {};
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    obj[k] = new Set(Array.isArray(raw[k]) ? raw[k] : []);
  });
  return obj;
};

const Students = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterOptions, setFilterOptions] = useState(null);

  // Pagination
  const [page, setPage] = useSessionState('students:page', 1);
  const [pageSize, setPageSize] = useSessionState('students:pageSize', 50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Search
  const [searchValue, setSearchValue] = useSessionState('students:search', '');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort
  const [sortBy, setSortBy] = useSessionState('students:sortBy', null);
  const [sortOrder, setSortOrder] = useSessionState('students:sortOrder', 'asc');

  // Filters
  const filtersOptions = useMemo(() => ({
    serialize: filtersSerialize,
    deserialize: filtersDeserialize,
  }), []);
  const [filters, setFilters] = useSessionState('students:filters', DEFAULT_FILTERS, filtersOptions);

  // Modals
  const [viewModalOpened, setViewModalOpened] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formModalOpened, setFormModalOpened] = useState(false);

  const abortControllerRef = useRef(null);

  // Загрузка filter-options
  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await getStudentFilterOptions();
      setFilterOptions(data);
    } catch (err) {
      console.error('Failed to load student filter options:', err);
    }
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    // Если хоть один фильтр = __none__, результат пуст
    const hasNoneFilter = Object.values(filters).some((f) => f && f.has('__none__'));
    if (hasNoneFilter) {
      setStudents([]); setTotal(0); setTotalPages(1); setLoading(false);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      // Фильтры по is_active → boolean
      let isActive = null;
      const activeFilter = filters.is_active;
      if (activeFilter && activeFilter.size > 0 && !activeFilter.has('__none__')) {
        if (activeFilter.has('true') && !activeFilter.has('false')) isActive = true;
        else if (activeFilter.has('false') && !activeFilter.has('true')) isActive = false;
      }

      // Фильтры по именам (для серверной фильтрации)
      const orgFilter = filters.organizations;
      const orgNames = (orgFilter && orgFilter.size > 0 && !orgFilter.has('__none__'))
        ? [...orgFilter].filter((v) => v !== '__empty__') : null;

      const groupFilter = filters.study_groups;
      const groupNames = (groupFilter && groupFilter.size > 0 && !groupFilter.has('__none__'))
        ? [...groupFilter].filter((v) => v !== '__empty__') : null;

      const parentFilter = filters.parents;
      const parentNames = (parentFilter && parentFilter.size > 0 && !parentFilter.has('__none__'))
        ? [...parentFilter].filter((v) => v !== '__empty__') : null;

      const data = await getStudents({
        page,
        pageSize,
        search: debouncedSearch.trim() || null,
        isActive,
        sortBy,
        sortOrder,
        organizationNames: orgNames,
        groupNames: groupNames,
        parentNames: parentNames,
        signal: controller.signal,
      });

      setStudents(data.items);
      setTotal(data.total);
      setTotalPages(data.pages);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching students:', err);
      setError(err.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortBy, sortOrder, filters]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleFiltersChange = (newFilters) => { setFilters(newFilters); setPage(1); };
  const handleSortChange = (field, order) => { setSortBy(field); setSortOrder(order); setPage(1); };
  const handleSearchChange = (value) => { setSearchValue(value); setPage(1); };
  const handlePageSizeChange = (value) => { setPageSize(Number(value)); setPage(1); };

  const handleRowClick = (student) => {
    setSelectedStudent(student);
    setViewModalOpened(true);
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Ученики
            </Title>
            <Text c="dimmed" size="sm">Всего: {total}</Text>
          </Box>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setFormModalOpened(true)}>
            Добавить ученика
          </Button>
        </Group>

        {/* Table */}
        {error ? (
          <Center p="xl"><Text c="red">{error}</Text></Center>
        ) : (
          <>
            <StudentsTable
              data={students}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              filterOptions={filterOptions}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              searchValue={searchValue}
              onRowClick={handleRowClick}
            />

            {/* Pagination */}
            <Group justify="space-between" mt="md">
              <Group gap="xs">
                <Text size="sm" c="dimmed">Показывать:</Text>
                <Select
                  value={pageSize.toString()}
                  onChange={handlePageSizeChange}
                  data={[
                    { value: '25', label: '25' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' },
                  ]}
                  size="xs"
                  style={{ width: 80 }}
                />
              </Group>

              {totalPages > 1 && (
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                  siblings={1}
                  boundaries={1}
                />
              )}

              <Text size="sm" c="dimmed">Страница {page} из {totalPages}</Text>
            </Group>

            {loading && students.length > 0 && (
              <Center><Loader size="sm" /></Center>
            )}
          </>
        )}

        {loading && students.length === 0 && !error && (
          <Center p="xl"><Loader size="lg" /></Center>
        )}
      </Stack>

      {/* Student View Modal */}
      <StudentViewModal
        opened={viewModalOpened}
        onClose={() => { setViewModalOpened(false); setSelectedStudent(null); }}
        student={selectedStudent}
        onDataChange={fetchStudents}
      />

      {/* Student Create Modal */}
      <StudentFormModal
        opened={formModalOpened}
        onClose={() => setFormModalOpened(false)}
        onSuccess={fetchStudents}
      />
    </Box>
  );
};

export default Students;
